import { isDefined, required as stashRequired, ValidationRules, wholeNumber } from '@leaflink/stash/useValidation';
import { MaybeRefOrGetter, toValue } from 'vue';

import {
  ProductCategory,
  UnifiedProductLine,
  UnifiedProductSubCategory,
  UnifiedProductTemplate,
  UnifiedUnitOfMeasure,
} from '@/clients/unifiedApi';
import { greaterThanOrEqualToField, lessThanOrEqualToField } from '@/composables/validation';
import { ValidationGroup, ValidationSchema, Validator } from '@/composables/validation/types';
import { minValue, positiveNumber, required } from '@/composables/validation/validators';
import { GoogleAddress } from '@/models/address';
import { Brand, Company, License } from '@/models/company';
import { StrainClassification, ThresholdAction } from '@/models/inventory';
import { ListingState, UnitDenomination } from '@/models/product';
import i18n from '@/plugins/i18n';
import { sanitizeDecimal } from '@/utils/i18n';

import { BulkEditModalFormState } from '../CustomMenu/CustomMenuAddEdit.types';

type FacilityValidationGroup = ValidationGroup & {
  $params: {
    companyState: Company['state'];
  };
};

const isAddressValid: Validator = {
  $message: i18n.t('settings.facilityManagement.invalidAddress') as string,
  $validator: (selectedAddress: GoogleAddress, validationGroup: FacilityValidationGroup) => {
    const { state, zipcode } = selectedAddress;
    const isAddressInSellerState = state === validationGroup.$params.companyState;

    return !!(zipcode && isAddressInSellerState);
  },
};

export function facilityValidationSchema(): Readonly<ValidationSchema> {
  return Object.freeze({
    address: {
      isAddressValid,
      required,
    },
    facilityName: {
      required,
    },
  });
}

export function metrcMappingValidationSchema(): Readonly<ValidationSchema> {
  return Object.freeze({
    conversionAmount: {
      required,
      positiveNumber,
    },
  });
}

export function editAllocationsModalItemValidationSchema(): Readonly<ValidationSchema> {
  return Object.freeze({
    quantity: {
      minVal: minValue(0),
      maxVal: {
        $validator(_val, form) {
          const val = Number(_val);
          const productCaseQuantity = Number(form.$params.productCaseQuantity);

          if (!val || !productCaseQuantity) {
            return true;
          }

          return val <= productCaseQuantity;
        },
        $message: (form) => i18n.t('validation.maxVal', { value: form.$params.productCaseQuantity }),
      },
    },
    expirationType: {},
    expirationDate: {},
    groupOrBuyer: {},
  });
}

export const editProductsModalValidationSchema: Readonly<ValidationSchema> = {
  listingState: {},
  wholesalePrice: {
    positiveNumber,
  },
  salePrice: {
    positiveNumber,
  },
  retailPrice: {
    positiveNumber,
  },
  minimumOrder: {
    positiveNumber,
    lessThanOrEqualToField: lessThanOrEqualToField({
      field: 'maximumOrder',
      message: i18n.t('inventory.editProductsModal.lessThanMaximumOrder'),
    }),
  },
  maximumOrder: {
    positiveNumber,
    greaterThanOrEqualToField: greaterThanOrEqualToField({
      field: 'minimumOrder',
      message: i18n.t('inventory.editProductsModal.greaterThanMinimumOrder'),
    }),
  },
};

// #region Add Strain Modal
export interface AddStrainFormState {
  name: string;
  classification: string;
}

export interface AddStrainValidationValues {
  name: AddStrainFormState['name'];
  classification: AddStrainFormState['classification'];
}

export function getAddStrainValidationRules(): ValidationRules<AddStrainValidationValues> {
  return {
    name: [stashRequired()],
    classification: [
      {
        name: 'isClassificationRequired',
        validator(value) {
          return value === '' ? true : isDefined(value);
        },
        message: i18n.t('common.required'),
      },
    ],
  };
}
// #endregion

export interface ProductTemplateFormState
  extends Pick<
    Partial<UnifiedProductTemplate>,
    | 'id'
    | 'template_name'
    | 'wholesale_price'
    | 'retail_price'
    | 'sale_price'
    | 'unit_multiplier'
    | 'inventory_management'
    | 'sell_in_multiples'
    | 'AVAILABLE_FOR_SAMPLES'
    | 'SHOW_QUANTITY'
    | 'allow_fractional_quantities'
    | 'sell_in_unit_of_measure'
    | 'seller'
    | 'threshold_value'
  > {
  unit_denomination?: UnitDenomination;
  listing_state?: ListingState;
  unit_of_measure?: UnifiedUnitOfMeasure;
  category?: ProductCategory;
  sub_category?: UnifiedProductSubCategory;
  brand?: Brand;
  product_line?: UnifiedProductLine;
  strain_classification?: StrainClassification;
  license?: License;
  threshold_action?: ThresholdAction;
  base_units_per_unit?: string;
  description?: string;
  maximum_order?: string;
  minimum_order?: string;
}

export function getProductTemplateValidationRules(
  formState: MaybeRefOrGetter<ProductTemplateFormState>,
): ValidationRules<ProductTemplateFormState> {
  return {
    template_name: [stashRequired()],
    category: [stashRequired()],
    unit_denomination: [stashRequired()],
    unit_of_measure: [
      stashRequired(),
      {
        name: 'unitOfMeasureIsDifferentThanCaseIfSellInMultiple',
        validator(value) {
          const form = toValue(formState);

          if (!form?.sell_in_multiples) {
            return true;
          }

          return !!value && value?.unit !== 'Case';
        },
        message: i18n.t('inventory.productTemplates.form.errors.unitOfMeasureIsDifferentThanCaseIfSellInMultiple'),
      },
      {
        name: 'unitOfMeasureIsDifferentThanCaseIfInheritedInventory',
        validator(value) {
          const form = toValue(formState);

          if (form?.inventory_management !== 'inherited') {
            return true;
          }

          return !!value && value?.unit !== 'Case';
        },
        message: i18n.t('inventory.productTemplates.form.errors.unitOfMeasureIsDifferentThanCaseIfInheritedInventory'),
      },
    ],
    unit_multiplier: [
      {
        name: 'isUnitMultiplierGreaterThanOne',
        validator(value) {
          if (!toValue(formState)?.sell_in_multiples) {
            return true;
          }

          return !!value && value > 1;
        },
        message: i18n.t('inventory.productTemplates.form.errors.isUnitMultiplierGreaterThanOne'),
      },
    ],
    maximum_order: [
      {
        name: 'maxOrderMustBeGreaterThanUnitMultiplier',
        validator(value) {
          const form = toValue(formState);

          if (!form?.sell_in_multiples) {
            return true;
          }

          return !!value && !!form?.unit_multiplier && parseFloat(value) > form.unit_multiplier;
        },
        message: i18n.t('inventory.productTemplates.form.errors.maxOrderMustBeGreaterThanUnitMultiplier'),
      },
      {
        name: 'maxOrderMustBeGreaterThanMinOrder',
        validator(value) {
          const form = toValue(formState);

          if (!value) {
            return true;
          }

          return !!form?.minimum_order && parseFloat(value) > parseFloat(form.minimum_order);
        },
        message: i18n.t('inventory.productTemplates.form.errors.maxOrderMustBeGreaterThanMinOrder'),
      },
    ],
    sale_price: [
      {
        name: 'salePriceMustBeLessThanWhosalePrice',
        validator(value) {
          const { wholesale_price } = toValue(formState);
          if (!value || value === '0.00' || !wholesale_price) {
            return true;
          }

          return parseFloat(value) < parseFloat(wholesale_price);
        },
        message: i18n.t('inventory.productTemplates.form.errors.salePriceMustBeLessThanWhosalePrice'),
      },
    ],
    wholesale_price: [stashRequired()],
    threshold_value: [wholeNumber()],
  };
}

export function getBulkEditModalValidationRules(): ValidationRules<BulkEditModalFormState> {
  return {
    discount: [
      {
        name: 'discountIsGreaterThanOrEqualToZero',
        validator(discount) {
          if (!discount?.value) return false;

          const sanitizedDiscount = sanitizeDecimal(discount?.value ?? '') || NaN;
          return Number(sanitizedDiscount) >= 0;
        },
        message: i18n.t('inventory.custom_menus.bulk_edit_error'),
      },
      {
        name: 'discountIsAValidNumber',
        validator(discount) {
          const sanitizedDiscount = sanitizeDecimal(discount?.value ?? '') || NaN;
          return !isNaN(Number(sanitizedDiscount));
        },
        message: i18n.t('validation.invalidNumber'),
      },
    ],
  };
}
